import { type MeiaSearchResultDto, MeiaSearchResultType } from '@@types/api.types';
import { Modal } from '@components/core/modal.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@components/ui/command';
import { useGetMeiaSearch } from '@queries/meia.query';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MEIA_SEARCH_MIN_QUERY_LENGTH, meiaRoute } from '../meia.constants';

type MeiaSearchPaletteProps = {
  isOpen: boolean;
  //
  onClose: () => void;
};

const SEARCH_DEBOUNCE_MS = 300;

const RESULT_GROUPS: Array<{ heading: string; type: MeiaSearchResultType }> = [
  { heading: 'Studies', type: MeiaSearchResultType.STUDY },
  { heading: 'Threads', type: MeiaSearchResultType.THREAD },
  { heading: 'Tasks', type: MeiaSearchResultType.TASK },
];

const buildSelectionQuery = (result: MeiaSearchResultDto): Record<string, string> => {
  const query: Record<string, string> = {};
  if (result.missionId) query.missionId = result.missionId;
  if (result.threadId) query.threadId = result.threadId;
  return query;
};

const resultHref = (result: MeiaSearchResultDto): string => {
  if (result.type === MeiaSearchResultType.THREAD) {
    return meiaRoute.studyTab({ query: buildSelectionQuery(result), studyId: result.studyId, tab: 'p2' });
  }
  if (result.type === MeiaSearchResultType.TASK) {
    return meiaRoute.studyTab({ query: buildSelectionQuery(result), studyId: result.studyId, tab: 'p3' });
  }
  return meiaRoute.studyTab({ studyId: result.studyId, tab: 'overview' });
};

// Rendered only while the modal is open. Closing unmounts it, so every open starts clean —
// no carried-over query text and no previous result set lingering from the last search.
const MeiaSearchPaletteBody: FC<{ onClose: () => void }> = (props) => {
  const { onClose } = props;
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const term = debouncedQuery.trim();
  const isBelowMinLength = term.length < MEIA_SEARCH_MIN_QUERY_LENGTH;
  const { data, isFetching } = useGetMeiaSearch({ q: term });
  const results = isBelowMinLength ? [] : (data?.results ?? []);
  const hasResults = results.length > 0;

  const handleSelect = (result: MeiaSearchResultDto): void => {
    navigate(resultHref(result));
    onClose();
  };

  return (
    <Command shouldFilter={false}>
      <CommandInput
        autoFocus
        onValueChange={setQuery}
        placeholder="Search studies, threads, and tasks…"
        value={query}
      />
      <CommandList>
        {isBelowMinLength ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">
            Type at least {MEIA_SEARCH_MIN_QUERY_LENGTH} characters to search.
          </div>
        ) : null}

        {!isBelowMinLength && !hasResults && isFetching ? (
          <div className="px-4 py-6 text-center text-sm text-gray-500">Searching…</div>
        ) : null}

        {!isBelowMinLength && !hasResults && !isFetching ? <CommandEmpty>No matches found.</CommandEmpty> : null}

        {RESULT_GROUPS.map((group) => {
          const items = results.filter((result) => result.type === group.type);
          if (items.length === 0) return null;
          return (
            <CommandGroup
              heading={<BlueLabel text={group.heading} />}
              key={group.type}
            >
              {items.map((result) => (
                <CommandItem
                  key={`${result.type}:${result.id}`}
                  onSelect={() => handleSelect(result)}
                  value={`${result.type}:${result.id}`}
                >
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate">{result.label}</span>
                    {result.type !== MeiaSearchResultType.STUDY ? (
                      <span className="truncate text-xs text-gray-500">{result.studyName}</span>
                    ) : null}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          );
        })}
      </CommandList>
    </Command>
  );
};

export const MeiaSearchPalette: FC<MeiaSearchPaletteProps> = (props) => {
  const { isOpen, onClose } = props;

  return (
    <Modal
      contentClassname="p-0"
      isOpen={isOpen}
      maxWidth="92vw"
      minWidth="320px"
      onClose={onClose}
      width="640px"
    >
      <MeiaSearchPaletteBody onClose={onClose} />
    </Modal>
  );
};
